/**
 * Reservation Statistics por **Source** — extracción y resolución de canales.
 *
 * Es el mismo reporte `res_statistics2` del que sale Diamond, pero emitido con
 * `Option Source` y filtro `Calendar/Month`: una fila por SOURCE con las noches
 * (`No. of Rooms`) de TODO el mes, en vez de una columna por día.
 *
 * De él salen los cuatro canales de venta que sigue la cadena:
 *
 * | Canal                      | 5 regionales | Caracas    |
 * | -------------------------- | ------------ | ---------- |
 * | `CDR` Central de Reservas  | `CDR`        | `CR`       |
 * | `VIP`                      | `VIP`        | `CORP VIP` |
 * | `VTA` Ventas               | `VTA`        | `CORPCCS`  |
 * | `FRO` Front Desk           | `FRO`        | `WI`       |
 *
 * **Cada sede escribe distinto lo mismo, y por eso el mapa es por código, no por
 * sede.** Los dos vocabularios no se solapan, así que una sola tabla de
 * equivalencias resuelve toda la cadena.
 *
 * Ojo con Caracas: tiene además un código `VEN` que *parece* «Ventas» por el
 * nombre y viene en 0 todos los meses — lo suyo se registra en `CORPCCS`
 * (decisión del operador, 2026-08-09). Mapear por parecido literal habría dejado
 * Ventas en cero en la sede más grande de la cadena sin un solo error visible.
 *
 * Lo que NO reconoce el mapa no se pierde ni se calla: cae en `OTROS` y sale
 * listado en `desconocidos` para que la revisión lo vea. Un código nuevo
 * (`RSL`, `AGV`, `GTE`, …) es una pregunta al operador, no un dato a repartir.
 *
 * Gemini SOLO lee y transcribe; la clasificación y las sumas se hacen aquí.
 */
import { z } from 'zod';
import { normalizeRateCode } from './res-statistics.js';
import { SEDE_CODES } from './types.js';

/**
 * Canales de venta que sigue la cadena. `OTROS` recoge todo lo demás.
 *
 * `EXP` (Expedia) **no sale de este reporte**: el PMS reparte las reservas de la
 * OTA entre sus códigos residuales y ninguno la aísla — en julio 2026, Caracas
 * marcó 7 noches como `EXP` cuando Expedia facturó 106. Lo alimenta su propio
 * reporte (`expedia.ts`) y el motor lo TALLA de `OTROS`, no lo suma encima; por
 * eso `EXP` no aparece en el mapa de códigos de abajo.
 */
export const CANALES = ['CDR', 'VIP', 'VTA', 'FRO', 'EXP', 'OTROS'] as const;
export type Canal = (typeof CANALES)[number];

/** Etiqueta en español de cada canal (la que ve el usuario). */
export const CANAL_LABEL: Record<Canal, string> = {
  CDR: 'Central de Reservas',
  VIP: 'VIP',
  VTA: 'Ventas',
  FRO: 'Front Desk',
  EXP: 'Expedia',
  OTROS: 'Otros',
};

/**
 * Fila que el reporte imprime como total de todas las sources. NO es un canal:
 * se guarda para cuadrar y como denominador del mix, y todo lector la separa.
 */
export const GRAND_TOTAL_SOURCE = 'GRAND_TOTAL';

/**
 * Equivalencias código → canal, sobre el código NORMALIZADO (mayúsculas, solo
 * A-Z0-9: `"CORP VIP"` → `CORPVIP`).
 *
 * Solo entra aquí lo observado en los reportes reales y las variantes
 * ortográficas de esa misma palabra. Un alias plausible pero no visto (`CRS`,
 * `AGV`, `RSL`…) se deja fuera a propósito: caer en `OTROS` y salir reportado es
 * recuperable; una equivalencia inventada mueve noches de canal en silencio.
 */
const CANAL_POR_CODIGO = new Map<string, Canal>([
  // Central de Reservas
  ['CDR', 'CDR'],
  ['CR', 'CDR'],
  ['CENTRALDERESERVAS', 'CDR'],
  // VIP
  ['VIP', 'VIP'],
  ['CORPVIP', 'VIP'],
  // Ventas
  ['VTA', 'VTA'],
  ['VTAS', 'VTA'],
  ['VEN', 'VTA'],
  ['VENTAS', 'VTA'],
  ['CORPCCS', 'VTA'],
  // Front Desk (incluye walk-in, que es la venta de mostrador)
  ['FRO', 'FRO'],
  ['FRONT', 'FRO'],
  ['FRONTDESK', 'FRO'],
  ['WI', 'FRO'],
  ['WKI', 'FRO'],
  ['WALKIN', 'FRO'],
]);

/** ¿Esta fila es el Grand Total impreso y no un canal? */
export function isGrandTotalSource(code: string): boolean {
  return normalizeRateCode(code) === 'GRANDTOTAL';
}

/** Canal al que pertenece un código de source. Lo no reconocido es `OTROS`. */
export function canalDeSource(code: string): Canal {
  return CANAL_POR_CODIGO.get(normalizeRateCode(code)) ?? 'OTROS';
}

/** ¿El mapa reconoce este código? (`false` ⇒ cayó en OTROS por defecto.) */
export function esCodigoConocido(code: string): boolean {
  return CANAL_POR_CODIGO.has(normalizeRateCode(code));
}

// ---------------------------------------------------------------------------
// Contrato de extracción
// ---------------------------------------------------------------------------

export const sourceRowSchema = z.object({
  /** Código tal cual aparece en el PDF (con espacios): es la auditoría. */
  code: z.string(),
  /** Columna "No. of Rooms" de esa source, para el mes completo. */
  rooms: z.number().int().nullable(),
});

export const sourceStatSchema = z.object({
  sede_codigo: z.enum(SEDE_CODES).nullable(),
  /** Año del filtro `Calendar/Month YYYY, MM`. */
  anio: z.number().int().nullable(),
  /** Mes 1..12 del mismo filtro. */
  mes: z.number().int().nullable(),
  sources: z.array(sourceRowSchema).default([]),
  /** Fila "Grand Total" → "No. of Rooms". Contexto y cuadre, no es un canal. */
  grand_total_rooms: z.number().int().nullable().default(null),
});

export type SourceStat = z.infer<typeof sourceStatSchema>;

export interface SourceRooms {
  code: string;
  canal: Canal;
  rooms: number;
}

export interface SourceStatResult {
  sedeCodigo: (typeof SEDE_CODES)[number] | null;
  anio: number | null;
  mes: number | null;
  /** Noches por canal; siempre trae las 5 claves, en 0 si el canal no aparece. */
  porCanal: Record<Canal, number>;
  /** Todas las sources del reporte, con el canal que les tocó. */
  sources: SourceRooms[];
  /** Σ de las sources (sin el Grand Total). */
  total: number;
  /** Grand Total impreso, o null si el reporte no lo trae. */
  grandTotal: number | null;
  /** Códigos que el mapa no reconoce (fueron a OTROS), con sus noches. */
  desconocidos: SourceRooms[];
  /** Avisos no bloqueantes, en español. */
  issues: string[];
}

function n(v: number | null | undefined): number {
  return v == null || !Number.isFinite(v) ? 0 : v;
}

/** Un objeto con las 5 claves en 0 (nunca `undefined` aguas abajo). */
export function canalesVacios(): Record<Canal, number> {
  return { CDR: 0, VIP: 0, VTA: 0, FRO: 0, EXP: 0, OTROS: 0 };
}

/**
 * Convierte la extracción en noches por canal.
 *
 * El cuadre `Σ sources = Grand Total` es el único control que detecta una fila
 * perdida o leída dos veces: el reporte imprime las dos cosas, así que un
 * descuadre siempre es un fallo de lectura, nunca del hotel. Avisa, no bloquea
 * (bloquear es decisión del commit).
 */
export function canalesFromSourceStat(g: SourceStat): SourceStatResult {
  const issues: string[] = [];
  const porCanal = canalesVacios();
  const sources: SourceRooms[] = [];
  const vistos = new Set<string>();

  for (const row of g.sources) {
    const code = row.code.trim();
    if (code === '') continue;
    if (isGrandTotalSource(code)) continue; // el total llega por su propio campo
    const key = normalizeRateCode(code);
    if (vistos.has(key)) {
      issues.push(`Source repetida en el reporte: ${code}.`);
      continue;
    }
    vistos.add(key);
    const canal = canalDeSource(code);
    const rooms = n(row.rooms);
    porCanal[canal] += rooms;
    sources.push({ code, canal, rooms });
  }

  const total = sources.reduce((s, r) => s + r.rooms, 0);
  const grandTotal = g.grand_total_rooms == null ? null : n(g.grand_total_rooms);
  const desconocidos = sources.filter((r) => !esCodigoConocido(r.code));

  if (!g.sede_codigo) issues.push('No se detectó la sede del reporte.');
  if (g.anio == null || g.mes == null) {
    issues.push('No se detectó el período (año y mes) del reporte.');
  } else if (g.mes < 1 || g.mes > 12) {
    issues.push(`Mes fuera de rango: ${g.mes}.`);
  }
  if (sources.length === 0) issues.push('El reporte no trae ninguna source.');
  if (grandTotal != null && total !== grandTotal) {
    issues.push(
      `Las sources suman ${total} y el Grand Total del reporte dice ${grandTotal} (diferencia ${total - grandTotal}). Falta o sobra alguna fila.`,
    );
  }
  const conNoches = desconocidos.filter((d) => d.rooms > 0);
  if (conNoches.length > 0) {
    issues.push(
      `Código(s) sin canal asignado, van a «Otros»: ${conNoches
        .map((d) => `${d.code} (${d.rooms})`)
        .join(', ')}.`,
    );
  }

  return {
    sedeCodigo: g.sede_codigo,
    anio: g.anio,
    mes: g.mes,
    porCanal,
    sources,
    total,
    grandTotal,
    desconocidos,
    issues,
  };
}

/** Prompt de extracción del "Reservation Statistics" por Source (mensual). */
export const GEMINI_SOURCE_PROMPT = `Eres un extractor de datos del reporte hotelero "Reservation Statistics" emitido por SOURCE (canal de venta), con filtro mensual.
Devuelve EXCLUSIVAMENTE un objeto JSON válido (sin markdown ni texto adicional) con este contrato.

Reglas:
1. Identifica la SEDE por el encabezado del reporte. Devuelve uno de: ${SEDE_CODES.join(', ')} en "sede_codigo" (o null si no es claro).
   - "Lidotel Hotel Boutique Margarita" => "MGTA"; "... Barquisimeto" => "BQTO"; "... Valencia" => "VAL"; "... San Cristobal" => "SC"; "Hotel Centro Lido" => "CCS"; "Agua Dorada" => "AD".
   - Si el encabezado dice solo "Lidotel Hotel Boutique" SIN ciudad, es Paraguaná => "PAR".
2. El PERÍODO está en el pie del reporte: "Filter Calendar/Month AAAA, MM" (ej. "Filter Calendar/Month 2026, 07" = julio de 2026). Devuelve "anio": 2026 y "mes": 7 (entero 1..12, sin cero a la izquierda). Si no aparece, usa null en ambos.
3. La primera columna de la tabla es "Source" y lista los canales de venta (CDR, VTA, FRO, VIP, WEB, CR, CORP VIP, CORPCCS, WI, RSL, AGV, HOU, INT, …). Para CADA source extrae ÚNICAMENTE la columna "No. of Rooms" como entero, en "sources": [{ "code": "CDR", "rooms": 398 }, …].
   - Copia el código TAL CUAL aparece, con sus espacios (ej. "CORP VIP").
   - NO extraigas Room Revenue, F&B Revenue, Misc Revenue, ADR, % Occ., No. of Guest, % Multi Occ., Single Occ. ni Multi Occ.
   - Incluye TODAS las sources, también las que están en 0.
4. Extrae la fila "Grand Total" → "No. of Rooms" en "grand_total_rooms" (número suelto, NO lo metas dentro de "sources").
5. El reporte puede tener VARIAS PÁGINAS: cuando una columna no cabe, la página siguiente REPITE la lista de sources con las columnas restantes (p. ej. solo "Multi Occ."). Esas páginas NO traen "No. of Rooms": ignóralas. Cada source debe aparecer UNA sola vez en "sources".
6. Alinea fila por fila: localiza la etiqueta de la izquierda y desplázate por SU MISMA fila hasta la columna "No. of Rooms". No mezcles valores de filas vecinas.
7. Si una celda está vacía o es 0, usa 0. No estimes ni calcules totales: solo transcribe lo que ves.`;
